// Two small pieces the contact Notes tab and the household Notes tab both
// need, kept together so the two surfaces can never drift apart on either.
//
// BOTH COME FROM ONE REPORT (Joy, 2026-08-03). She recorded a client's payment
// details on a contact, went looking for them on the household, found an empty
// Notes tab, wrote a test note there to check, saw THAT appear, and concluded
// notes weren't saving. Both notes had saved correctly the whole time — they
// were simply on different records, and neither tab admitted the other existed.
//
//   * OtherNotesPointer — says the other record has notes, and links to it.
//     Fixes the "where did it go?" failure without merging the two surfaces
//     (a household note and a note about one person are genuinely different
//     things; the fix is visibility, not collapsing them together).
//   * NotePiiWarning — the payment details themselves should never have been
//     in note text. This says so as you type, and names the field they belong
//     in, rather than silently accepting regulated PII into a plaintext column.

import type { ReactElement } from "react";
import { Link } from "react-router-dom";

import { PII_KIND_COPY, piiKindsInText, type PiiKind } from "../../../shared/contacts/pii-in-text";

/** Points at notes living on the OTHER record. Renders nothing when there
 *  are none, so a household with no member notes stays uncluttered. */
export function OtherNotesPointer({
  count,
  label,
  to,
}: {
  count: number;
  /** Plain-English description of where they are, e.g. "the Dorris
   *  household" or "Nancy Dorris". */
  label: string;
  to: string;
}): ReactElement | null {
  if (count <= 0) return null;
  return (
    <p
      className="muted"
      style={{ fontSize: 12.5, margin: "0 0 10px" }}
      data-testid="other-notes-pointer"
    >
      {count} {count === 1 ? "note is" : "notes are"} saved on <Link to={to}>{label}</Link> — notes
      live on the record they were written about.
    </p>
  );
}

/** Warns that the draft appears to contain regulated PII, and says where it
 *  actually belongs. Returns null (and reports no kinds) for ordinary text. */
export function NotePiiWarning({ text }: { text: string }): ReactElement | null {
  const kinds = piiKindsInText(text);
  if (kinds.length === 0) return null;
  return (
    <div
      role="status"
      data-testid="note-pii-warning"
      style={{
        marginTop: 6,
        padding: "8px 10px",
        border: "1px solid var(--mid-gray)",
        borderLeft: "3px solid var(--danger)",
        borderRadius: "var(--radius-md)",
        fontSize: 12.5,
        lineHeight: 1.45,
      }}
    >
      <strong>This looks like {listKinds(kinds)}.</strong>
      <div style={{ marginTop: 4 }}>
        Notes are stored as ordinary readable text — they aren't encrypted, and they show up in
        exports and summaries. Put it in {PII_KIND_COPY[kinds[0] as PiiKind].destination} instead,
        where it is encrypted and every look at it is logged.
      </div>
    </div>
  );
}

/** True when the composer should ask for an explicit second click. */
export function draftHasPii(text: string): boolean {
  return piiKindsInText(text).length > 0;
}

function listKinds(kinds: PiiKind[]): string {
  const labels = kinds.map((k) => PII_KIND_COPY[k].label);
  if (labels.length === 1) return labels[0] as string;
  if (labels.length === 2) return `${labels[0]} and ${labels[1]}`;
  return `${labels.slice(0, -1).join(", ")}, and ${labels[labels.length - 1]}`;
}
